/**
 * College Baseball (CBB) Roster Service
 *
 * Provides college baseball team roster data with player information.
 * Similar to mlbRosterService.js but for NCAA college baseball teams.
 *
 * @example
 * import { getCBBTeamRoster, CBB_TEAMS } from './cbbRosterService';
 * const duke = getCBBTeamRoster('duke');
 */

// =============================================================================
// CBB TEAM DEFINITIONS
// =============================================================================

export const CBB_TEAMS = {
  duke: {
    teamId: 'duke',
    ncaaId: 150,
    name: 'Duke Blue Devils',
    abbreviation: 'DUKE',
    conference: 'ACC',
    division: 'Division I',
    primaryColor: '#003087',
    secondaryColor: '#FFFFFF',
    logoUrl: '/logos/duke-d.png',
    rosterUrl: 'https://goduke.com/sports/baseball/roster',
    headshotBaseUrl: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/goduke.com/images/2025/12/16/',
  },
  unc: {
    teamId: 'unc',
    ncaaId: 457,
    name: 'North Carolina Tar Heels',
    abbreviation: 'UNC',
    conference: 'ACC',
    division: 'Division I',
    primaryColor: '#7BAFD4',
    secondaryColor: '#FFFFFF',
    logoUrl: '/logos/unc.png',
    rosterUrl: 'https://goheels.com/sports/baseball/roster',
    headshotBaseUrl: 'https://dxbhsrqyrr690.cloudfront.net/sidearm.nextgen.sites/unc.sidearmsports.com/images/2025/10/2/',
  },
};

// =============================================================================
// ABBREVIATION TO TEAM KEY MAPPING
// =============================================================================

// Keys are normalized: lowercase, spaces stripped — matches getCBBTeamRoster() normalization
export const CBB_ABBREV_TO_KEY = {
  'duke': 'duke',
  'duk': 'duke',
  'dukebluedevils': 'duke',
  'bluedevils': 'duke',
  'unc': 'unc',
  'northcarolina': 'unc',
  'tarheels': 'unc',
  'carolina': 'unc',
  'northcarolinatarheels': 'unc',
  'goheels': 'unc',
};

// =============================================================================
// CBB PLAYER ROSTERS (2026 Season Data)
// =============================================================================

const CBB_ROSTERS = {
  duke: [
    // Infielders
    { id: 'duke-1', firstName: 'Jeff', lastName: 'Lougee', position: 'INF', number: 1, year: 'So.', height: '6-0', weight: 185, hometown: 'Mechanicsburg, Pa.', imageId: 'JeffLougee' },
    { id: 'duke-2', firstName: 'Coltin', lastName: 'Quagliano', position: 'INF', number: 2, year: 'Gr.', height: '5-10', weight: 195, hometown: 'Kewanee, Ill.', imageId: 'ColtinQuagliano' },
    { id: 'duke-3', firstName: 'Jake', lastName: 'Lambdin', position: 'INF', number: 6, year: 'Jr.', height: '5-10', weight: 185, hometown: 'Rancho Santa Margarita, Calif.', imageId: 'JakeLambdin' },
    { id: 'duke-4', firstName: 'Ben', lastName: 'Petrone', position: 'INF', number: 10, year: 'Gr.', height: '6-0', weight: 185, hometown: 'Princeton, N.J.', imageId: 'BenPetrone' },
    { id: 'duke-5', firstName: 'Will', lastName: 'Shaheen', position: 'INF', number: 14, year: 'Fr.', height: '6-3', weight: 200, hometown: 'Portsmouth, N.H.', imageId: 'WillShaheen' },
    { id: 'duke-6', firstName: 'Andrew', lastName: 'Bell', position: 'INF', number: 16, year: 'So.', height: '6-1', weight: 195, hometown: 'Denver, Colo.', imageId: 'AndrewBell' },
    { id: 'duke-7', firstName: 'Collin', lastName: 'Anderson', position: 'INF', number: 32, year: 'Fr.', height: '5-10', weight: 170, hometown: 'Rockville Centre, N.Y.', imageId: 'CollinAnderson' },
    { id: 'duke-8', firstName: 'Jeff', lastName: 'Lougee', position: 'INF', number: 40, year: 'So.', height: '6-1', weight: 200, hometown: 'Rye, New York', imageId: 'JeffLougee' },
    { id: 'duke-9', firstName: 'RJ', lastName: 'Hamilton', position: 'INF/OF', number: 9, year: 'Jr.', height: '5-10', weight: 185, hometown: 'Hoover, Ala.', imageId: 'RJHamilton' },
    // Outfielders
    { id: 'duke-10', firstName: 'Adin', lastName: 'Zorn', position: 'OF', number: 3, year: 'Jr.', height: '6-3', weight: 205, hometown: 'Aliquippa, Pa.', imageId: 'AdinZorn' },
    { id: 'duke-11', firstName: 'Tyler', lastName: 'Albright', position: 'OF', number: 5, year: 'Sr.', height: '6-0', weight: 200, hometown: 'Greensboro, N.C.', imageId: 'TylerAlbright' },
    { id: 'duke-12', firstName: 'Trevor', lastName: 'Johnson', position: 'OF', number: 18, year: 'So.', height: '6-2', weight: 205, hometown: 'Bellefonte, Pa.', imageId: 'TrevorJohnson' },
    { id: 'duke-13', firstName: 'Bobby', lastName: 'Marsh', position: '1B/OF', number: 20, year: 'Gr.', height: '6-2', weight: 210, hometown: 'Bellefonte, Pa.', imageId: 'BobbyMarsh' },
    { id: 'duke-14', firstName: 'Michael', lastName: 'DiMartini', position: 'OF', number: 24, year: 'Jr.', height: '6-2', weight: 210, hometown: 'Pittsburgh, Pa.', imageId: 'MichaelDiMartini' },
    { id: 'duke-15', firstName: 'Adam', lastName: 'Troch', position: 'OF', number: 25, year: 'So.', height: '6-0', weight: 175, hometown: 'Bethesda, Md.', imageId: 'AdamTroch' },
    { id: 'duke-16', firstName: 'Brooks', lastName: 'Perez', position: '1B/OF', number: 27, year: 'Fr.', height: '6-3', weight: 200, hometown: 'Boerne, Texas', imageId: 'BrooksPerez' },
    { id: 'duke-17', firstName: 'Andrew', lastName: 'Duncan', position: 'OF', number: 31, year: 'Fr.', height: '6-2', weight: 205, hometown: 'Bethesda, Md.', imageId: 'AndrewDuncan' },
    // Catchers
    { id: 'duke-18', firstName: 'Matthew', lastName: 'Strand', position: 'C', number: 7, year: 'Gr.', height: '6-0', weight: 200, hometown: 'Suwanee, Ga.', imageId: 'MatthewStrand' },
    { id: 'duke-19', firstName: 'Cider', lastName: 'Canon', position: 'C/OF', number: 21, year: 'Jr.', height: '5-11', weight: 180, hometown: 'San Marino, Calif.', imageId: 'CiderCanon' },
    { id: 'duke-20', firstName: 'Anthony', lastName: 'Runfola', position: 'C/OF', number: 22, year: 'Gr.', height: '6-0', weight: 200, hometown: 'Phoenix, Ariz.', imageId: 'AnthonyRunfola' },
    { id: 'duke-21', firstName: 'Nolan', lastName: 'Johnson', position: 'C/1B', number: 28, year: 'So.', height: '6-6', weight: 215, hometown: 'Thousand Oaks, Calif.', imageId: 'NolanJohnson' },
    // Right-Handed Pitchers
    { id: 'duke-22', firstName: 'Jordan', lastName: 'Regulski', position: 'RHP', number: 8, year: 'Fr.', height: '6-3', weight: 175, hometown: 'Hurlock, Md.', imageId: 'JordanRegulski' },
    { id: 'duke-23', firstName: 'Aidan', lastName: 'Weaver', position: 'RHP', number: 11, year: 'Sr.', height: '6-5', weight: 230, hometown: 'Jamison, Pa.', imageId: 'AidanWeaver' },
    { id: 'duke-24', firstName: 'Will', lastName: 'Oschell', position: 'RHP', number: 17, year: 'Fr.', height: '6-4', weight: 205, hometown: 'Phoenixville, Pa.', imageId: 'WillOschell' },
    { id: 'duke-25', firstName: 'David', lastName: 'Boisvert', position: 'RHP', number: 26, year: 'Gr.', height: '6-5', weight: 215, hometown: 'Charlotte, N.C.', imageId: 'DavidBoisvert' },
    { id: 'duke-26', firstName: 'Caleb', lastName: 'Anderson', position: 'RHP', number: 29, year: 'Gr.', height: '6-0', weight: 205, hometown: 'Springdale, Ark.', imageId: 'CalebAnderson' },
    { id: 'duke-27', firstName: 'Peter', lastName: 'Lemke', position: 'RHP', number: 30, year: 'Jr.', height: '6-0', weight: 205, hometown: 'Geneva, Ill.', imageId: 'PeterLemke' },
    { id: 'duke-28', firstName: 'Matt', lastName: 'Nochowitz', position: 'RHP', number: 33, year: 'Jr.', height: '6-4', weight: 210, hometown: 'Glen Head, N.Y.', imageId: 'MattNochowitz' },
    { id: 'duke-29', firstName: 'Andy', lastName: 'Leon', position: 'RHP', number: 35, year: 'Gr.', height: '6-1', weight: 215, hometown: 'Sugar Land, Texas', imageId: 'AndyLeon' },
    { id: 'duke-30', firstName: 'Roman', lastName: 'DiGiacomo', position: 'RHP', number: 37, year: 'Sr.', height: '6-3', weight: 220, hometown: 'Fairfield, Conn.', imageId: 'RomanDeGiacomo' },
    { id: 'duke-31', firstName: 'Sammy', lastName: 'Petrocelli', position: 'RHP', number: 38, year: 'So.', height: '6-2', weight: 220, hometown: 'Los Angeles, Calif.', imageId: 'SammyPetrocelli' },
    { id: 'duke-32', firstName: 'Nate', lastName: 'Brittain', position: 'RHP', number: 41, year: 'Jr.', height: '6-4', weight: 200, hometown: 'Mount Holly, N.C.', imageId: 'NateBrittain' },
    { id: 'duke-33', firstName: 'Jack', lastName: 'Hedrick', position: 'RHP', number: 43, year: 'So.', height: '6-5', weight: 205, hometown: 'Charlotte, N.C.', imageId: 'JackHedrick' },
    { id: 'duke-34', firstName: 'Ben', lastName: 'Dean', position: 'RHP', number: 44, year: 'So.', height: '6-3', weight: 220, hometown: 'Swanzey, N.H.', imageId: 'BenDean' },
    { id: 'duke-35', firstName: 'Harley', lastName: 'Goddner', position: 'RHP', number: 46, year: 'Gr.', height: '6-0', weight: 210, hometown: 'Sanford, N.C.', imageId: 'HarleyGoddner' },
    { id: 'duke-36', firstName: 'Drew', lastName: 'Bryan', position: 'RHP', number: 48, year: 'So.', height: '6-3', weight: 200, hometown: 'Mason, Ohio', imageId: 'DrewBryan' },
    { id: 'duke-37', firstName: 'Bryce', lastName: 'Brannon', position: 'RHP', number: 49, year: 'Gr.', height: '6-3', weight: 205, hometown: 'Newton Square, Pa.', imageId: 'BryceBrannon' },
    { id: 'duke-38', firstName: 'Grayson', lastName: 'Walker', position: 'RHP', number: 50, year: 'Fr.', height: '6-1', weight: 190, hometown: 'Philadelphia, Pa.', imageId: 'GraysonWalker' },
    // Left-Handed Pitchers
    { id: 'duke-39', firstName: 'Edward', lastName: 'Hart', position: 'LHP', number: 12, year: 'Sr.', height: '6-5', weight: 235, hometown: 'San Antonio, Texas', imageId: 'EdwardHart' },
    { id: 'duke-40', firstName: 'Bennett', lastName: 'LaPalm', position: 'LHP', number: 34, year: 'Jr.', height: '6-1', weight: 185, hometown: 'Bellbrook, Ohio', imageId: 'BennettLaPalm' },
    { id: 'duke-41', firstName: 'Aiden', lastName: "O'Connell", position: 'LHP', number: 36, year: 'So.', height: '6-2', weight: 190, hometown: 'Bedford, N.H.', imageId: 'AidenOConnell' },
    { id: 'duke-42', firstName: 'Jack', lastName: 'Feehery', position: 'LHP', number: 39, year: 'So.', height: '6-0', weight: 175, hometown: 'Washington, D.C.', imageId: 'JackFeehery' },
    { id: 'duke-43', firstName: 'Marcello', lastName: 'Mastroianni', position: 'LHP', number: 42, year: 'Jr.', height: '6-3', weight: 195, hometown: 'Little Silver, N.J.', imageId: 'MarcelloMastroianni' },
    { id: 'duke-44', firstName: 'Eamon', lastName: 'McDermott', position: 'LHP', number: 51, year: 'Fr.', height: '6-2', weight: 195, hometown: 'Boston, Mass.', imageId: 'EamonMcDermott' },
  ],
  unc: [
    // Infielders
    { id: 'unc-1', firstName: 'Cooper', lastName: 'Nicholson', position: 'INF', number: 1, year: 'Jr.', height: '5-11', weight: 195, hometown: 'West Des Moines, Iowa', imageId: 'CooperNicholson' },
    { id: 'unc-2', firstName: 'Jake', lastName: 'Schaffner', position: 'INF', number: 2, year: 'Jr.', height: '6-2', weight: 175, hometown: 'Janesville, Wis.', imageId: 'JakeSchaffner' },
    { id: 'unc-3', firstName: 'Lee', lastName: 'Sowers', position: 'INF', number: 3, year: 'So.', height: '5-10', weight: 185, hometown: 'Glen Allen, Va.', imageId: 'LeeSowers' },
    { id: 'unc-5', firstName: 'Gavin', lastName: 'Gallaher', position: 'INF', number: 5, year: 'Jr.', height: '6-1', weight: 191, hometown: 'Apex, N.C.', imageId: 'GavinGallaher' },
    { id: 'unc-11', firstName: 'Matt', lastName: 'McKnight', position: 'INF', number: 11, year: 'Fr.', height: '6-3', weight: 195, hometown: 'Charlotte, N.C.', imageId: 'MattMcKnight' },
    { id: 'unc-20', firstName: 'AJ', lastName: 'Terry', position: 'INF', number: 20, year: 'Fr.', height: '6-0', weight: 190, hometown: 'Randolph, N.J.', imageId: 'AJTerry' },
    { id: 'unc-44', firstName: 'Erik', lastName: 'Paulsen', position: 'INF', number: 44, year: 'Jr.', height: '6-3', weight: 215, hometown: 'Massapequa, N.Y.', imageId: 'ErikPaulsen' },
    // Outfielders
    { id: 'unc-4', firstName: 'Sawyer', lastName: 'Black', position: 'OF', number: 4, year: 'So.', height: '6-1', weight: 194, hometown: 'Oak Ridge, N.C.', imageId: 'SawyerBlack' },
    { id: 'unc-7', firstName: 'Perry', lastName: 'Hargett', position: 'OF', number: 7, year: 'So.', height: '6-0', weight: 187, hometown: 'Peachland, N.C.', imageId: 'PerryHargett' },
    { id: 'unc-8', firstName: 'Owen', lastName: 'Hull', position: 'OF', number: 8, year: 'Jr.', height: '6-4', weight: 215, hometown: 'Alexandria, Va.', imageId: 'OwenHull' },
    { id: 'unc-12', firstName: 'Tyler', lastName: 'Howe', position: 'OF/1B', number: 12, year: 'Fr.', height: '5-11', weight: 185, hometown: 'Huntersville, N.C.', imageId: 'TylerHowe' },
    { id: 'unc-14', firstName: 'Michael', lastName: 'Maginnis', position: 'OF', number: 14, year: 'Gr.', height: '6-1', weight: 210, hometown: 'Newnan, Ga.', imageId: 'MichaelMaginnis' },
    { id: 'unc-18', firstName: 'Carter', lastName: 'French', position: 'OF', number: 18, year: 'Sr.', height: '6-2', weight: 196, hometown: 'Tampa, Fla.', imageId: 'CarterFrench' },
    { id: 'unc-28', firstName: 'Jadyn', lastName: 'Nunez', position: 'OF', number: 28, year: 'Fr.', height: '6-1', weight: 185, hometown: 'Miami, Fla.', imageId: 'JadynNunez' },
    // Catchers
    { id: 'unc-6', firstName: 'Macon', lastName: 'Winslow', position: 'C', number: 6, year: 'Jr.', height: '6-0', weight: 200, hometown: 'Hertford, N.C.', imageId: 'MaconWinslow' },
    { id: 'unc-23', firstName: 'Colin', lastName: 'Hynek', position: 'C', number: 23, year: 'Gr.', height: '6-1', weight: 200, hometown: 'Rockmart, Ga.', imageId: 'ColinHynek' },
    { id: 'unc-25', firstName: 'Rom', lastName: 'Kellis V', position: 'OF/C', number: 25, year: 'Sr.', height: '6-1', weight: 203, hometown: 'Gilbert, S.C.', imageId: 'RomKellisV' },
    // Right-Handed Pitchers
    { id: 'unc-15', firstName: 'Cameron', lastName: 'Padgett', position: 'RHP', number: 15, year: 'Sr.', height: '6-3', weight: 195, hometown: 'Salisbury, N.C.', imageId: 'CameronPadgett' },
    { id: 'unc-17', firstName: 'Talan', lastName: 'Holiday', position: 'RHP', number: 17, year: 'Fr.', height: '6-2', weight: 190, hometown: 'Pembroke Pines, Fla.', imageId: 'TalanHoliday' },
    { id: 'unc-19', firstName: 'Camron', lastName: 'Seagraves', position: 'RHP', number: 19, year: 'So.', height: '6-3', weight: 179, hometown: 'Ellerbe, N.C.', imageId: 'CamronSeagraves' },
    { id: 'unc-24', firstName: 'Matthew', lastName: 'Matthijs', position: 'RHP', number: 24, year: 'Sr.', height: '6-2', weight: 230, hometown: 'Greenville, N.C.', imageId: 'MatthewMatthijs' },
    { id: 'unc-27', firstName: 'Caden', lastName: 'Glauber', position: 'RHP', number: 27, year: 'Fr.', height: '6-4', weight: 210, hometown: 'Fort Mill, S.C.', imageId: 'CadenGlauber' },
    { id: 'unc-29', firstName: 'Jason', lastName: 'DeCaro', position: 'RHP', number: 29, year: 'Jr.', height: '6-5', weight: 230, hometown: 'North Port, N.Y.', imageId: 'JasonDeCaro' },
    { id: 'unc-31', firstName: 'Olin', lastName: 'Johnson', position: 'RHP', number: 31, year: 'Jr.', height: '6-6', weight: 230, hometown: 'Bellingham, Wash.', imageId: 'OlinJohnson' },
    { id: 'unc-39', firstName: 'Jake', lastName: 'Cackovic', position: 'RHP', number: 39, year: 'Fr.', height: '6-7', weight: 215, hometown: 'Holly Springs, N.C.', imageId: 'JakeCackovic' },
    { id: 'unc-40', firstName: 'Walker', lastName: 'McDuffie', position: 'RHP', number: 40, year: 'So.', height: '6-0', weight: 200, hometown: 'Broadway, N.C.', imageId: 'WalkerMcDuffie' },
    { id: 'unc-41', firstName: 'Justin', lastName: 'Mabe', position: 'RHP', number: 41, year: 'Fr.', height: '6-3', weight: 185, hometown: 'Lexington, N.C.', imageId: 'JustinMabe' },
    { id: 'unc-42', firstName: 'Boston', lastName: 'Flannery', position: 'RHP', number: 42, year: 'Jr.', height: '6-4', weight: 230, hometown: 'New City, N.Y.', imageId: 'BostonFlannery' },
    { id: 'unc-43', firstName: 'Riley', lastName: 'Leatherman', position: 'RHP', number: 43, year: 'R-Fr.', height: '6-2', weight: 180, hometown: 'Maple Grove, Minn.', imageId: 'RileyLeatherman' },
    { id: 'unc-47', firstName: 'Sean', lastName: 'Hurley', position: 'RHP', number: 47, year: 'Fr.', height: '6-5', weight: 205, hometown: 'Cohoes, N.Y.', imageId: 'SeanHurley' },
    { id: 'unc-49', firstName: 'Amos', lastName: 'Rich', position: 'RHP', number: 49, year: 'Fr.', height: '6-2', weight: 200, hometown: 'Waynesville, N.C.', imageId: 'AmosRich' },
    { id: 'unc-52', firstName: 'Andrew', lastName: 'Wallen', position: 'RHP', number: 52, year: 'R-Fr.', height: '6-7', weight: 245, hometown: 'Greenville, N.C.', imageId: 'AndrewWallen' },
    { id: 'unc-53', firstName: 'Ryan', lastName: 'Lynch', position: 'RHP', number: 53, year: 'So.', height: '6-4', weight: 235, hometown: 'Moorestown, N.J.', imageId: 'RyanLynch' },
    // Left-Handed Pitchers
    { id: 'unc-10', firstName: 'Tom', lastName: 'Chmielewski', position: 'LHP', number: 10, year: 'Gr.', height: '6-2', weight: 210, hometown: 'Needham, Mass.', imageId: 'TomChmielewski' },
    { id: 'unc-26', firstName: 'Kyle', lastName: 'Percival', position: 'LHP', number: 26, year: 'Sr.', height: '6-5', weight: 220, hometown: 'Lancaster, S.C.', imageId: 'KylePercival' },
    { id: 'unc-35', firstName: 'Jackson', lastName: 'Rose', position: 'LHP', number: 35, year: 'Fr.', height: '6-3', weight: 205, hometown: 'Woodstock, Ga.', imageId: 'JacksonRose' },
    { id: 'unc-36', firstName: 'Folger', lastName: 'Boaz', position: 'LHP', number: 36, year: 'Jr.', height: '6-2', weight: 212, hometown: 'Pilot Mountain, N.C.', imageId: 'FolgerBoaz' },
    { id: 'unc-51', firstName: 'Tim', lastName: 'Lawson', position: 'LHP', number: 51, year: 'R-Fr.', height: '6-5', weight: 230, hometown: 'Fort Myers, Fla.', imageId: 'TimLawson' },
  ],
};

// =============================================================================
// ROSTER LOOKUP FUNCTIONS
// =============================================================================

/**
 * Get player headshot URL from SideArm Sports CDN.
 * Pattern: {team.headshotBaseUrl}{imageId}.png
 * Works for any SideArm-powered school (Duke, UNC, etc.)
 */
function getHeadshotUrl(imageId, teamConfig) {
  if (!imageId || !teamConfig?.headshotBaseUrl) return null;
  return `${teamConfig.headshotBaseUrl}${imageId}.png`;
}

/**
 * Get full roster for a college baseball team with headshot URLs.
 *
 * @param {string} teamKey - Team identifier (e.g., 'duke')
 * @returns {Array} - Array of player objects with headshot URLs
 */
export function getCBBTeamRoster(teamKey) {
  const normalizedKey = teamKey?.toLowerCase()?.replace(/\s+/g, '');
  // Try direct key first, then abbreviation mapping
  const actualKey = CBB_ROSTERS[normalizedKey] ? normalizedKey : CBB_ABBREV_TO_KEY[normalizedKey];
  const roster = CBB_ROSTERS[actualKey];
  const team = CBB_TEAMS[actualKey];

  if (!roster || !team) {
    console.warn(`[CBBRoster] Team not found: ${teamKey}`);
    return [];
  }

  // Enrich each player with headshot URL and team info
  return roster.map((player) => ({
    id: player.id,
    player_id: player.id,
    first_name: player.firstName,
    last_name: player.lastName,
    name: `${player.firstName} ${player.lastName}`,
    Player: `${player.firstName} ${player.lastName}`,
    position: player.position,
    Position: player.position,
    position_abbreviation: player.position,
    number: player.number,
    PlayerNo: player.number,
    year: player.year,
    class: player.year,
    height: player.height,
    Height: player.height,
    weight: player.weight,
    Weight: `${player.weight} lbs`,
    hometown: player.hometown,
    team: team.name,
    team_id: team.teamId,
    team_abbreviation: team.abbreviation,
    TeamAbbrev: team.abbreviation,
    // Headshot URLs
    image_url: getHeadshotUrl(player.imageId, team),
    photo: getHeadshotUrl(player.imageId, team),
    imageUrl: getHeadshotUrl(player.imageId, team),
    image: getHeadshotUrl(player.imageId, team),
    // Generated stats for demo (would come from NCAA stats in production)
    stats: generateDemoStats(player.position),
    ...generateDemoStats(player.position),
  }));
}

/**
 * Get team info by teamKey
 */
export function getCBBTeamInfo(teamKey) {
  return CBB_TEAMS[teamKey?.toLowerCase()] || null;
}

/**
 * Check if team is a CBB team
 */
export function isCBBTeam(teamKey) {
  const normalizedKey = teamKey?.toLowerCase()?.replace(/\s+/g, '');
  return normalizedKey in CBB_TEAMS || normalizedKey in CBB_ABBREV_TO_KEY;
}

/**
 * Get all available CBB teams
 */
export function getAvailableCBBTeams() {
  return Object.entries(CBB_TEAMS).map(([key, team]) => ({
    team_id: key,
    team_name: team.name,
    abbreviation: team.abbreviation,
    logo_url: team.logoUrl,
    team_color: team.primaryColor,
    ncaaId: team.ncaaId,
    conference: team.conference,
    division: team.division,
  }));
}

/**
 * Search players across all CBB teams
 */
export function searchCBBPlayers(query) {
  const normalizedQuery = query?.toLowerCase() || '';
  const results = [];

  Object.entries(CBB_ROSTERS).forEach(([teamKey, roster]) => {
    const team = CBB_TEAMS[teamKey];
    roster.forEach(player => {
      const fullName = `${player.firstName} ${player.lastName}`.toLowerCase();
      if (fullName.includes(normalizedQuery) || player.lastName.toLowerCase().includes(normalizedQuery)) {
        results.push({
          ...player,
          name: `${player.firstName} ${player.lastName}`,
          team: team.name,
          team_id: teamKey,
          imageUrl: getHeadshotUrl(player.imageId, team),
        });
      }
    });
  });

  return results;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Generate demo stats based on position (for display purposes)
 * In production, this would come from NCAA stats
 */
function generateDemoStats(position) {
  const isPitcher = ['SP', 'RP', 'P', 'RHP', 'LHP'].includes(position);

  if (isPitcher) {
    return {
      era: (Math.random() * 3 + 2).toFixed(2),
      ERA: (Math.random() * 3 + 2).toFixed(2),
      wins: Math.floor(Math.random() * 10 + 2),
      losses: Math.floor(Math.random() * 6 + 1),
      strikeouts: Math.floor(Math.random() * 80 + 40),
      whip: (Math.random() * 0.4 + 0.9).toFixed(2),
      innings_pitched: (Math.random() * 60 + 30).toFixed(1),
      k_per_9: (Math.random() * 4 + 7).toFixed(1),
    };
  } else {
    return {
      batting_average: (Math.random() * 0.1 + 0.26).toFixed(3),
      AVG: (Math.random() * 0.1 + 0.26).toFixed(3),
      home_runs: Math.floor(Math.random() * 15 + 2),
      rbi: Math.floor(Math.random() * 50 + 15),
      ops: (Math.random() * 0.3 + 0.7).toFixed(3),
      stolen_bases: Math.floor(Math.random() * 15),
      runs: Math.floor(Math.random() * 40 + 20),
    };
  }
}

/**
 * Get position group for filtering
 */
export function getPositionGroup(position) {
  const pos = position?.toUpperCase();
  if (['SP', 'RP', 'P', 'RHP', 'LHP'].includes(pos)) return 'Pitchers';
  if (['C'].includes(pos) || pos?.includes('C/')) return 'Catchers';
  if (['1B', '2B', '3B', 'SS', 'INF'].includes(pos) || pos?.includes('INF')) return 'Infielders';
  if (['LF', 'CF', 'RF', 'OF'].includes(pos) || pos?.includes('OF')) return 'Outfielders';
  if (['DH'].includes(pos)) return 'Designated Hitters';
  return 'Other';
}

/**
 * Get available position filters for college baseball
 */
export function getCollegeBaseballPositionFilters() {
  return [
    { key: 'all', label: 'All Positions' },
    { key: 'pitchers', label: 'Pitchers', positions: ['SP', 'RP', 'P', 'RHP', 'LHP'] },
    { key: 'catchers', label: 'Catchers', positions: ['C'] },
    { key: 'infielders', label: 'Infielders', positions: ['1B', '2B', '3B', 'SS', 'INF'] },
    { key: 'outfielders', label: 'Outfielders', positions: ['LF', 'CF', 'RF', 'OF'] },
  ];
}

export default {
  getCBBTeamRoster,
  getCBBTeamInfo,
  isCBBTeam,
  getAvailableCBBTeams,
  searchCBBPlayers,
  getPositionGroup,
  getCollegeBaseballPositionFilters,
  CBB_TEAMS,
};
